import { HttpClient, HttpContext, HttpResourceRef, httpResource } from "@angular/common/http";
import { Injectable, Signal, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { CACHE_CONFIG } from "@kno2/core/http";
import { filterNonEmptyProperties } from "@kno2/utils";
import { Observable, firstValueFrom } from "rxjs";
import { DownloadableFile, DownloadableFilePagedResult, DownloadsQuery } from "./downloads.models";

@Injectable({
    providedIn: "root"
})
export class DownloadsService {
    private httpClient = inject(HttpClient);

    public getPages(query: Signal<DownloadsQuery>): HttpResourceRef<DownloadableFilePagedResult> {
        return httpResource(() => ({
            url: "/api/downloads/getpaged",
            params: filterNonEmptyProperties(query())
        }));
    }

    public getAllByTag(tag: Signal<string>): HttpResourceRef<DownloadableFile[]> {
        return httpResource(() => (tag() ? { url: "/api/downloads/tag", params: { name: tag() } } : undefined), { defaultValue: [] });
    }

    public getAll = (): Signal<DownloadableFile[]> => toSignal(this.getAll$());
    public upload = (download: DownloadableFile, file: File): Promise<void> => firstValueFrom(this.upload$(download, file));
    public update = (download: DownloadableFile): Promise<void> => firstValueFrom(this.update$(download));
    public delete = (id: number): Promise<void> => firstValueFrom(this.delete$(id));
    public activate = (id: number): Promise<void> => firstValueFrom(this.activate$(id));
    public deactivate = (id: number): Promise<void> => firstValueFrom(this.deactivate$(id));

    private getAll$(): Observable<DownloadableFile[]> {
        return this.httpClient.get<DownloadableFile[]>("/api/downloads", {
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
        });
    }

    private getById$(id: number): Observable<DownloadableFile> {
        return this.httpClient.get<DownloadableFile>(`/api/downloads/${id}`);
    }

    private upload$(downloadableFileResource: DownloadableFile, file: File): Observable<void> {
        const formData = new FormData();

        formData.append("downloadableFileResource", JSON.stringify(downloadableFileResource));
        formData.append("file", file);

        return this.httpClient.post<void>("/api/downloads/upload", formData);
    }

    private update$(downloadableFileResource: DownloadableFile): Observable<void> {
        return this.httpClient.put<void>(`/api/downloads/${downloadableFileResource.id}/update`, downloadableFileResource);
    }

    private delete$(id: number): Observable<void> {
        return this.httpClient.delete<void>(`/api/downloads/${id}`);
    }

    private activate$(id: number): Observable<void> {
        return this.httpClient.put<void>(`/api/downloads/${id}/activate`, null);
    }

    private deactivate$(id: number): Observable<void> {
        return this.httpClient.put<void>(`/api/downloads/${id}/deactivate`, null);
    }
}
